import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Download, ChevronDown, ShieldCheck, Star, Users } from "lucide-react";

const ICONS = { master: ShieldCheck, supervisor: Star, end_user: Users };
const ACCENT = { master: "#F59E0B", supervisor: "#A78BFA", end_user: "#22D3EE" };

function GuideCard({ g }) {
  const [open, setOpen] = useState(false);
  const [chapter, setChapter] = useState(0);
  const [busy, setBusy] = useState(false);
  const Icon = ICONS[g.key] || BookOpen;
  const accent = ACCENT[g.key] || "#22D3EE";
  const downloadPdf = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await client.get(`/guides/${g.key}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `MATCOM_${g.key}_guide.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${g.title} PDF downloaded`);
    } catch { toast.error("PDF download failed"); }
    setBusy(false);
  };
  return (
    <div className="border bg-[#141518] rounded-sm" style={{ borderColor: open ? accent : "#2C2D35" }}>
      <button data-testid={`guide-card-${g.key}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
        <Icon size={18} style={{ color: accent }} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-head font-bold text-sm text-white">{g.title}</div>
          <div className="font-mono text-[10px] text-[#9CA3AF] truncate">{g.subtitle}</div>
        </div>
        <button data-testid={`guide-pdf-${g.key}`} onClick={downloadPdf} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-head font-bold rounded-sm shrink-0 text-black disabled:opacity-50"
          style={{ background: accent }}>
          <Download size={12} /> {busy ? "…" : "PDF"}
        </button>
        <ChevronDown size={15} className="text-[#9CA3AF] shrink-0 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div className="border-t border-[#2C2D35] flex flex-col sm:flex-row" data-testid={`guide-body-${g.key}`}>
          <div className="sm:w-56 shrink-0 border-b sm:border-b-0 sm:border-r border-[#2C2D35] p-2 space-y-0.5 max-h-72 sm:max-h-none overflow-y-auto">
            {g.chapters.map((ch, i) => (
              <button key={i} data-testid={`guide-${g.key}-ch-${i}`} onClick={() => setChapter(i)}
                className={`w-full text-left px-2.5 py-1.5 font-mono text-[10px] rounded-sm leading-snug ${i === chapter ? "text-black font-bold" : "text-[#9CA3AF] hover:text-white"}`}
                style={i === chapter ? { background: accent } : {}}>
                {ch.title}
              </button>
            ))}
          </div>
          <div className="flex-1 p-4 space-y-4 max-h-[28rem] overflow-y-auto">
            <div className="font-head font-bold text-sm" style={{ color: accent }}>{g.chapters[chapter].title}</div>
            {g.chapters[chapter].sections.map((s, i) => (
              <div key={i}>
                <div className="font-head font-bold text-xs text-white mb-1.5">{s.heading}</div>
                <ul className="space-y-1">
                  {s.body.map((b, j) => (
                    <li key={j} className="text-[13px] text-[#C9CDD4] leading-relaxed flex gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const UserGuides = () => {
  const [guides, setGuides] = useState([]);
  useEffect(() => { client.get("/guides").then((r) => setGuides(r.data.guides)).catch(() => {}); }, []);
  if (!guides.length) return null;
  return (
    <div className="space-y-2.5 mb-6" data-testid="user-guides">
      <div className="flex items-center gap-2">
        <BookOpen size={15} className="text-[#22D3EE]" />
        <span className="font-head font-bold text-xs text-white tracking-wider">OFFICIAL USER GUIDES</span>
        <span className="font-mono text-[9px] text-[#6B7280]">BROWSE IN-APP OR DOWNLOAD AS PDF</span>
      </div>
      {guides.map((g) => <GuideCard key={g.key} g={g} />)}
    </div>
  );
};
